import { Editor, useEditorState } from "@tiptap/react"
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  BetweenHorizontalEnd,
  BetweenVerticalEnd,
  Bold,
  Code,
  Code2,
  Columns2,
  Eraser,
  Grid2x2Plus,
  Heading,
  ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Maximize2,
  Minimize2,
  Minus,
  PenTool,
  Quote,
  Redo2,
  Rows3,
  Scaling,
  Sigma,
  SquareCode,
  Strikethrough,
  Trash2,
  Underline,
  Undo2,
  Unlink2,
} from "lucide-react"
import EditorToolbarButton from "./EditorToolbarButton"
import EditorToolbarAction from "./EditorToolbarAction"
import EditorHeadingSelect from "./EditorHeadingSelect"

type EditorToolbarProps = {
  editor: Editor | null
  isHtmlMode?: boolean
  isFullscreen?: boolean
  isImageResizeEnabled?: boolean
  isUploadingImage?: boolean
  onToggleHtmlMode: () => void
  onToggleFullscreen: () => void
  onToggleImageResize: () => void
  onInsertImage: () => void
  onInsertEquation: () => void
  onInsertDiagram: () => void
  onInsertLink: () => void
  onInsertTable: () => void
}

type HeadingLevel = "paragraph" | 1 | 2 | 3

const getHeadingLevel = (editor: Editor): HeadingLevel => {
  for (const level of [1, 2, 3] as const) {
    if (editor.isActive("heading", { level })) {
      return level
    }
  }

  return "paragraph"
}

const applyHeadingLevel = (editor: Editor, level: HeadingLevel) => {
  if (level === "paragraph") {
    editor.chain().focus().setParagraph().run()
    return
  }

  editor.chain().focus().setHeading({ level }).run()
}

const EditorToolbar = ({
  editor,
  isHtmlMode = false,
  isFullscreen = false,
  isImageResizeEnabled = false,
  isUploadingImage = false,
  onToggleHtmlMode,
  onToggleFullscreen,
  onToggleImageResize,
  onInsertImage,
  onInsertEquation,
  onInsertDiagram,
  onInsertLink,
  onInsertTable,
}: EditorToolbarProps) => {
  const toolbarState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      if (!currentEditor) {
        return null
      }

      return {
        canUndo: currentEditor.can().undo(),
        canRedo: currentEditor.can().redo(),
        headingLevel: getHeadingLevel(currentEditor),
        isBold: currentEditor.isActive("bold"),
        isItalic: currentEditor.isActive("italic"),
        isUnderline: currentEditor.isActive("underline"),
        isStrike: currentEditor.isActive("strike"),
        isCode: currentEditor.isActive("code"),
        isCodeBlock: currentEditor.isActive("codeBlock"),
        isBlockquote: currentEditor.isActive("blockquote"),
        isLink: currentEditor.isActive("link"),
        isBulletList: currentEditor.isActive("bulletList"),
        isOrderedList: currentEditor.isActive("orderedList"),
        isTable: currentEditor.isActive("table"),
        isAlignLeft: currentEditor.isActive({ textAlign: "left" }),
        isAlignCenter: currentEditor.isActive({ textAlign: "center" }),
        isAlignRight: currentEditor.isActive({ textAlign: "right" }),
        isAlignJustify: currentEditor.isActive({ textAlign: "justify" }),
      }
    },
  })

  const handleClearFormatting = () => {
    if (!editor) return

    editor.chain().focus().clearNodes().unsetAllMarks().run()
  }

  const runTableCommand = (
    command: "addColumnAfter" | "addRowAfter" | "deleteColumn" | "deleteRow" | "toggleHeaderRow" | "deleteTable"
  ) => {
    if (!editor) return
    editor.chain().focus()[command]().run()
  }

  if (!editor || !toolbarState) return null

  return (
    <div className='tiptap-editor__toolbar' role='toolbar' aria-label='Formatting'>
      <div className='tiptap-editor__toolbar-group'>
        <EditorToolbarButton
          icon={<Undo2 size={16} />}
          label='Undo'
          onClick={() => editor.chain().focus().undo().run()}
          disabled={isHtmlMode || !toolbarState.canUndo}
        />
        <EditorToolbarButton
          icon={<Redo2 size={16} />}
          label='Redo'
          onClick={() => editor.chain().focus().redo().run()}
          disabled={isHtmlMode || !toolbarState.canRedo}
        />
      </div>

      <div className='tiptap-editor__toolbar-divider' />

      <div className='tiptap-editor__toolbar-group'>
        <EditorHeadingSelect
          value={toolbarState.headingLevel}
          disabled={isHtmlMode}
          onChange={(level) => applyHeadingLevel(editor, level)}
        />
      </div>

      <div className='tiptap-editor__toolbar-divider' />

      <div className='tiptap-editor__toolbar-group'>
        <EditorToolbarButton
          icon={<Bold size={16} />}
          label='Bold'
          isActive={toolbarState.isBold}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<Italic size={16} />}
          label='Italic'
          isActive={toolbarState.isItalic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<Underline size={16} />}
          label='Underline'
          isActive={toolbarState.isUnderline}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<Strikethrough size={16} />}
          label='Strikethrough'
          isActive={toolbarState.isStrike}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<Code size={16} />}
          label='Inline code'
          isActive={toolbarState.isCode}
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<Eraser size={16} />}
          label='Clear formatting'
          onClick={handleClearFormatting}
          disabled={isHtmlMode}
        />
      </div>

      <div className='tiptap-editor__toolbar-divider' />

      <div className='tiptap-editor__toolbar-group'>
        <EditorToolbarButton
          icon={<List size={16} />}
          label='Bullet list'
          isActive={toolbarState.isBulletList}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<ListOrdered size={16} />}
          label='Ordered list'
          isActive={toolbarState.isOrderedList}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<Quote size={16} />}
          label='Blockquote'
          isActive={toolbarState.isBlockquote}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<SquareCode size={16} />}
          label='Code block'
          isActive={toolbarState.isCodeBlock}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<Minus size={16} />}
          label='Horizontal rule'
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          disabled={isHtmlMode}
        />
      </div>

      <div className='tiptap-editor__toolbar-divider' />

      <div className='tiptap-editor__toolbar-group'>
        <EditorToolbarButton
          icon={<AlignLeft size={16} />}
          label='Align left'
          isActive={toolbarState.isAlignLeft}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<AlignCenter size={16} />}
          label='Align center'
          isActive={toolbarState.isAlignCenter}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<AlignRight size={16} />}
          label='Align right'
          isActive={toolbarState.isAlignRight}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<AlignJustify size={16} />}
          label='Justify'
          isActive={toolbarState.isAlignJustify}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          disabled={isHtmlMode}
        />
      </div>

      <div className='tiptap-editor__toolbar-divider' />

      <div className='tiptap-editor__toolbar-group'>
        <EditorToolbarButton
          icon={<Link2 size={16} />}
          label='Insert link'
          isActive={toolbarState.isLink}
          onClick={onInsertLink}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<Unlink2 size={16} />}
          label='Remove link'
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={isHtmlMode || !toolbarState.isLink}
        />
      </div>

      <div className='tiptap-editor__toolbar-divider' />

      <div className='tiptap-editor__toolbar-group'>
        <EditorToolbarButton
          icon={<Grid2x2Plus size={16} />}
          label='Insert table'
          isActive={toolbarState.isTable}
          onClick={onInsertTable}
          disabled={isHtmlMode}
        />
        <EditorToolbarButton
          icon={<BetweenVerticalEnd size={16} />}
          label='Add column after'
          onClick={() => runTableCommand("addColumnAfter")}
          disabled={isHtmlMode || !toolbarState.isTable}
        />
        <EditorToolbarButton
          icon={<BetweenHorizontalEnd size={16} />}
          label='Add row after'
          onClick={() => runTableCommand("addRowAfter")}
          disabled={isHtmlMode || !toolbarState.isTable}
        />
        <EditorToolbarButton
          icon={<Columns2 size={16} />}
          label='Delete column'
          onClick={() => runTableCommand("deleteColumn")}
          disabled={isHtmlMode || !toolbarState.isTable}
        />
        <EditorToolbarButton
          icon={<Rows3 size={16} />}
          label='Delete row'
          onClick={() => runTableCommand("deleteRow")}
          disabled={isHtmlMode || !toolbarState.isTable}
        />
        <EditorToolbarButton
          icon={<Heading size={16} />}
          label='Toggle header row'
          onClick={() => runTableCommand("toggleHeaderRow")}
          disabled={isHtmlMode || !toolbarState.isTable}
        />
        <EditorToolbarButton
          icon={<Trash2 size={16} />}
          label='Delete table'
          onClick={() => runTableCommand("deleteTable")}
          disabled={isHtmlMode || !toolbarState.isTable}
        />
      </div>

      <div className='tiptap-editor__toolbar-divider' />

      <div className='tiptap-editor__toolbar-group tiptap-editor__toolbar-group--content'>
        <EditorToolbarButton
          icon={isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          isActive={isFullscreen}
          onClick={onToggleFullscreen}
        />
        <EditorToolbarAction
          icon={<ImageIcon size={15} />}
          label='Upload image'
          text={isUploadingImage ? "Uploading…" : "Image"}
          onClick={onInsertImage}
          disabled={isHtmlMode || isUploadingImage}
        />
        <EditorToolbarAction
          icon={<Scaling size={15} />}
          label={
            isImageResizeEnabled ? "Disable image resize" : "Enable image resize"
          }
          text='Resize'
          isActive={isImageResizeEnabled}
          onClick={onToggleImageResize}
          disabled={isHtmlMode}
        />
        <EditorToolbarAction
          icon={<Code2 size={15} />}
          label={isHtmlMode ? "Back to visual editor" : "Edit in HTML"}
          text={isHtmlMode ? "Visual" : "Edit HTML"}
          isActive={isHtmlMode}
          onClick={onToggleHtmlMode}
        />
        <EditorToolbarAction
          icon={<Sigma size={15} />}
          label='Insert equation or chemical formula'
          text='Equation'
          onClick={onInsertEquation}
          disabled={isHtmlMode}
        />
        <EditorToolbarAction
          icon={<PenTool size={15} />}
          label='Insert geometry drawing or graph'
          text='Diagram'
          onClick={onInsertDiagram}
          disabled={isHtmlMode}
        />
      </div>
    </div>
  )
}

export default EditorToolbar
